//------------------------------------------------------------------------------
// Node Modules ----------------------------------------------------------------
import React from 'react';
import classNames from 'classnames';
import { Helmet } from 'react-helmet';
import FocusTrap from 'focus-trap-react';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';

//------------------------------------------------------------------------------
// Style -----------------------------------------------------------------------
import styles from './index.module.scss';
//------------------------------------------------------------------------------
// My Components ---------------------------------------------------------------
import { Modal, Button, ButtonStyle, QrCode } from '@cmp/common';
import {
  QRCodeFlyerHeader,
  QRCodeFlyerFooter,
  QRCodeFlyerProducts,
} from '@cmp/images';
//------------------------------------------------------------------------------
// Helpers ---------------------------------------------------------------------
import { withTranslation } from 'react-i18next';
import { StoreConfigContext } from '@helpers/storeConfigContext';
import { getValue } from '@helpers/localStorage';
// API -------------------------------------------------------------------------
import { loadProviders } from '@api/endpoints/get/providers';
//------------------------------------------------------------------------------
import { refCode } from '@helpers/constants/localStorage';
import { QRCodeFlyerJP1, QRCodeFlyerJP2 } from '../../../../images';
//------------------------------------------------------------------------------
// React Class -----------------------------------------------------------------
class ProfessionalCodeShare extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      qrCodeLoading: false,
      providerDetails: null,
    };

    this.onSendCode = this.onSendCode.bind(this);
    this.onDownloadQRCode = this.onDownloadQRCode.bind(this);
    this.mountTrap = this.mountTrap.bind(this);
    this.unmountTrap = this.unmountTrap.bind(this);
  }

  componentDidMount() {
    const { history, location } = this.props;
    const {
      featureFlags: { shareQRCodeEnabled },
    } = this.context;

    if (!shareQRCodeEnabled) {
      history.push({
        pathname: '/recommendation/professional-code',
        state: {
          from: location.state.from,
          modal: true,
        },
      });
      return;
    }

    this.mountTrap();
    this.loadProviderDetails();
  }

  loadProviderDetails() {
    loadProviders({ showAccountInfo: true }).then((result) => {
      this.setState({ providerDetails: result.data.accountInfo });
    });
  }

  mountTrap() {
    this.setState({ activeTrap: true });
  }

  unmountTrap() {
    this.setState({ activeTrap: false });
  }

  headerComponent() {
    const { t } = this.props;
    const code = getValue(refCode);

    return (
      <div className={styles.Header}>
        <h2 className={styles.Title}>{t('dashboard_ShareCode')}</h2>
        <span className={styles.Subtitle}>
          {t('dashboard_ShareCodeSubtitle', {
            code,
          })}
        </span>
      </div>
    );
  }

  onSendCode() {
    const { history, location } = this.props;

    history.push({
      pathname: '/recommendation/professional-code',
      state: {
        from: location.state.from,
        modal: true,
      },
    });
  }

  QRFlyer = ({ referralCode, name, email, language, t, hCode }) => {
    const { QRFlyerEn, QRFlyerJp } = this;
    return language === 'ja' ? (
      <QRFlyerJp
        id="referralQRCode"
        containerStyle={classNames(
          styles.DownloadableQRJp,
          styles.DownloadableQR
        )}
        referralCode={referralCode}
        discountAmount={20}
        name={name}
        email={email}
        t={t}
        hCode={hCode}
      />
    ) : (
      <QRFlyerEn
        id="referralQRCode"
        containerStyle={styles.DownloadableQR}
        referralCode={referralCode}
        discountAmount={30}
        name={name}
        email={email}
        language={language}
        t={t}
      />
    );
  };

  QRFlyerEn = ({
    id,
    containerStyle,
    referralCode,
    discountAmount,
    name,
    email,
    t,
  }) => {
    return (
      <div id={id} className={containerStyle}>
        <div className={styles.Hero}>
          <QRCodeFlyerHeader className={styles.QRCodeFlyerHeader} />
          <div className={styles.QRContainerTitle}>
            <div className={styles.SubTitle}>
              {' '}
              {t('dashboard_FlyerSubTitle')}{' '}
            </div>
            <div className={styles.Title}> {t('header_title')} </div>
          </div>
          <QRCodeFlyerProducts className={styles.QRCodeFlyerProducts} />
          <QRCodeFlyerFooter className={styles.QRCodeFlyerFooter} />
          <div className={styles.HeroContent}>
            <div className={styles.HeroTitle}>
              {t('dashboard_FlyerHeroTitle')}
            </div>
            <div className={styles.ClinicDetails}>
              <div className={styles.ClinicDetailsTitle}>
                {t('dashboard_FlyerGetRecommendations')}
              </div>
              <div className={styles.ClinicDetailsTitle}>{name}</div>
              <div>
                <span>{referralCode}</span>{' '}
                <span className={styles.ClinicCode}>
                  {t('dashboard_FlyerClinicCode')}
                </span>
              </div>
              <div>{email}</div>
            </div>
          </div>
        </div>
        <div className={styles.DiscountContent}>
          <div className={styles.DiscountTitle}>
            {t('dashboard_FlyerDiscountTitle')}
          </div>
          <div className={styles.DiscountAmount}> {discountAmount} </div>
          <div className={styles.DiscountPercentage}>%</div>
          <div> {t('dashboard_FlyerDiscountDetails1')} </div>
          <div> {t('dashboard_FlyerDiscountDetails2')} </div>
        </div>
        <div className={styles.QRContainer}>
          <div className={styles.QRContainerTitle}>
            {t('dashboard_FlyerQRCodeScan')}
          </div>
          <QrCode
            url={`${
              import.meta.env.VITE_SHOPIFY_PUBLIC_URL
            }/pages/ref?${referralCode}`}
            showDownload={false}
            height="400px"
            onLoaded={() => this.setState({ qrCodeLoading: false })}
          />
          <div>
            {t('dashboard_FlyerQRCodeDomain', {
              code: referralCode,
            })}
          </div>
        </div>
      </div>
    );
  };

  QRFlyerJp = ({ id, containerStyle, referralCode, name, email, t, hCode }) => {
    let ClinicDetailsTitleClasses = classNames(styles.ClinicDetailsTitle, {
      [styles.ClinicDetailsTitleSmallFont]: name.length > 26,
      [styles.ClinicDetailsTitle4Lines]: name.length > 70,
    });
    return (
      <>
        <div id={id} className={containerStyle}>
          <QRCodeFlyerJP1 />
          <div>
            <div className={styles.ClinicDetails}>
              <span className={styles.ClinicName}> {name} </span>
              <span className={styles.ClinicDetailsTitle}>{referralCode}</span>
            </div>
            <div className={styles.ClinicHCodeDetails}>
              <span className={styles.ClinicName}> {name} </span>
              <span className={styles.ClinicDetailsTitle}>
                {hCode?.slice(1, hCode.length)}
              </span>
            </div>
          </div>
        </div>
        <div id={`${id}2`} className={containerStyle}>
          <QRCodeFlyerJP2 />
        </div>
      </>
    );
  };

  qrComponent() {
    const { t } = this.props;
    const { providerDetails } = this.state;
    const { QRFlyer } = this;
    const {
      storeConfig: { language },
    } = this.context;
    const referralCode = getValue(refCode);

    return providerDetails == null ? (
      <div></div>
    ) : (
      <QRFlyer
        referralCode={referralCode}
        name={providerDetails?.practiceName}
        email={providerDetails?.email}
        language={language}
        t={t}
        hCode={providerDetails?.practiceNumber}
      />
    );
  }

  async onDownloadQRCode() {
    const { t } = this.props;

    this.setState({ qrCodeLoading: true });

    const pdf = new jsPDF({
      unit: 'pt',
      format: 'a5',
      userUnit: 600,
      compress: true,
    });
    const width = pdf.internal.pageSize.getWidth();
    const height = pdf.internal.pageSize.getHeight();

    let page = document.getElementById('referralQRCode');
    let canvas = await html2canvas(page, { dpi: 600 });
    let imgData = canvas.toDataURL('image/png');
    pdf.addImage(imgData, 'PNG', 0, 0, width, height);

    page = document.getElementById('referralQRCode2');
    if (page) {
      canvas = await html2canvas(page, { dpi: 600 });
      imgData = canvas.toDataURL('image/png');
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, 0, width, height);
    }

    const fileName = 'dashboard_DownloadQRCodeFileName';
    pdf.save(`${t(fileName)}.pdf`);
    this.setState({ qrCodeLoading: false });
  }

  formComponent() {
    const { t } = this.props;
    const { qrCodeLoading, providerDetails } = this.state;
    const loading = qrCodeLoading || providerDetails === null;

    return (
      <div className={styles.Form}>
        <div className={styles.SubmitArea}>
          <Button
            style={ButtonStyle.Primary}
            className={styles.Button}
            onClick={this.onSendCode}
          >
            {t('dashboard_SendReferralCodeEmail')}
          </Button>
          <Button
            style={ButtonStyle.Outlined}
            className={styles.Button}
            onClick={this.onDownloadQRCode}
            onKeyPress={this.onDownloadQRCode}
            loading={loading}
            disabled={loading}
            id="focusedButton"
          >
            {t('dashboard_DownloadReferralCode')}
          </Button>
        </div>
      </div>
    );
  }

  render() {
    const { history, location, t } = this.props;
    const componentClasses = classNames(styles.Parent);

    return (
      <FocusTrap>
        <Modal.Base
          className={componentClasses}
          onClose={() => history.push(location.state.from)}
        >
          <Helmet>
            <title>{t('dashboard_ShareCode')}</title>
          </Helmet>

          {this.headerComponent()}
          {this.formComponent()}
          {this.qrComponent()}
        </Modal.Base>
      </FocusTrap>
    );
  }
}
//------------------------------------------------------------------------------
// Export ----------------------------------------------------------------------
export default withTranslation()(ProfessionalCodeShare);

ProfessionalCodeShare.contextType = StoreConfigContext;
